import { useState, useEffect, } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { calculateFinancialSummary, 
  formatCurrency, 
  getVal, 
  isNegative, 
} from '../../helpers/process_morningstar.js';
import { StrategyContext } from "../Context/StrategyContext";
import { format, isValid, subMonths, subDays, set } from "date-fns";
import axios from "axios";
import pLimit from "p-limit";


import {
  Box,
  Container,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper
} from "@mui/material";

const WEBAPI = process.env.REACT_APP_WEB_API;

const headers = [
    "Overall",
    "Return on Capital",
    "Balance Sheet",
    "Growth",
    "Valuation",   
]

const CAExchanges = ["TSX", "V", "TSE", "CNQ"];

function backgroundColor(value) {
  // Handle invalid or missing values
  if ((value) === null || value === undefined || value === "") return "#ffffffff"; // gray fallback for bad input

  const colorRanges = [
    "#ff0000", // 0–10
    "#ff6a00ff", // 11–20
    "#a87c1c", // 21–30
    "#948222", // 31–40
    "#768c2b", // 41–50
    "#679030", // 51–60
    "#589534", // 61–70
    "#3f9c3c", // 71–80
    "#18a848", // 81–90
    "#0bac4c", // 91–100
  ];

  // Clamp the value between 0–100
  const clamped = Math.min(Math.max(value, 0), 100);

  // Find which range it's in
  const index = Math.min(Math.floor(clamped / 10), colorRanges.length - 1);

  return colorRanges[index];
}

function formatValue(v) {
  const format = Number(v).toFixed(1) + "%";
  return format;
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const responsiveStyle = {
  whiteSpace: "normal",
  width: "auto",
  maxWidth: '160px',
  wordBreak:"break-word",
}

export default function Scorecard() {
    const { ticker } = useParams()
    // const { name, market, sectors, marketCapMin, marketCapMax, factors, topN } = useContext(StrategyContext);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState("");
    const [periodList, setPeriodList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [topNCompanies, setTopNCompanies] = useState([]);
    const [tables, setTables] = useState([]);
    const [error, setError] = useState(null);
    const [scorecardData, setScorecardData] = useState([]);
    const [Tax, setTax] = useState(getVal("selectedTax"));
    const [capRD, setCapRD] = useState(getVal("capRD"));
    const [includeOtherIncome, setIncludeOtherIncome] = useState(getVal("includeOtherIncome"));

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "capRD") setCapRD(JSON.parse(event.newValue));
      if (event.key === "includeOtherIncome") setIncludeOtherIncome(JSON.parse(event.newValue));
    };

    const handleCustomUpdate = () => {
      setCapRD(getVal("capRD"));
      setIncludeOtherIncome(getVal("includeOtherIncome"));
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageUpdated", handleCustomUpdate);
    };
  }, []);  

   useEffect(() => {
 
     setLoading(true);
     setPeriod(""); // We need this. If we don't have it the Period UseEffect won't fire. Load a strategy and leave on the first period. Change the Strategy and the table will not change.
 
     const periodsListTmp = [];
     const startDate = new Date();
     let periodValue = 0;
 
     for (var x = 0; x < 10; x++) {
       let nextDate = subMonths(startDate, x * 3);
       while (!isValid(nextDate)) {
         subDays(nextDate, 1);
       }
 
       periodsListTmp.push({ value: String(periodValue).padStart(2, "0"), label: format(nextDate, "MMM dd, yyyy") });
       //console.log(periodsListTmp);
       periodValue++;
     }
     periodsListTmp.reverse();
     setPeriodList(periodsListTmp);

     setPeriod("00");
   }, []);

useEffect(() => {
  const fetchScorecardData = async () => {
    if (!periodList?.length || !ticker) return;
    setLoading(true);

    const exchange = ticker.split(".")[1].toUpperCase();
    const isCA = CAExchanges.includes(exchange);

    const limit = pLimit(5); // Limit to 5 concurrent requests

    const FIXED_PERIODS = ["00","01","02","03","04","05","06","07","08","09"];
    const validPeriod = (val) => FIXED_PERIODS.includes(val) ? val : "00";

    const payload = {
      market: isCA ? "CAN" : "USA",
      sectors: isCA
        ? "CD,CS,TE,HC,IN,IT,UT,FT,EN,MA"
        : "CD,CS,TE,EN,HC,IN,IT,MA,UT,FT",
      mcap_min: isCA ? 500 : 20000,
      mcap_max: 10000000,
      metrics:
        "CFIRREX_TTM,CFIRR_TTM,CFIRREXCHG_TTMvPQ,CFIRRCHG_TTMvPY,IAGFACHG_ANQvPY,CURRENTRATIO_ANQ,DEBTTOMARKET_ANQ,REVENUECHG_TTMvPY,BASICCFCHG_TTMvPY,EVEBITDA_ANQ,PBOOK_ANQ",
      ranks: 
        "DESC,DESC,DESC,DESC,DESC,DESC,DESC,DESC,DESC,ASC,ASC",
      weights: "30,30,10,10,5,5,5,20,20,10,10",
      period: "00,01,02,03,04,05,06,07,08,09",
      tableColumns:
        "QSCFIRREX.TTM,QSCFIRR.TTM,QSCFIRREXCHG.TTMvPQ,QSCFIRRCHG.TTMvPY,QSINFLADJGROSSCHG.ANQvPY,QSCURRENTRATIO.ANQ,QSDEBTTOMARKET.ANQ,QSREVENUECHG.TTMvPY,QSBASICCFCHG.TTMvPY,QSEVEBITDA.ANQ,QSPBOOK.ANQ",
      minimums: ",,,,,,,,,,",
      maximums: ",,,,,,,,,,",
    }

    const promises = periodList.map((period) =>
      limit(async () => {
        const periodVal = validPeriod(period.value);

        const res = await axios.post(`${WEBAPI}/quickstrategy/results`, {
          ...payload,
          period: periodVal,
        });

       // console.log('Scorecard response:', res.data);

        const stock = res.data.find(
          (obj) => obj.Ticker === ticker.toUpperCase()
        );
        if (!stock) return null;

        return {
          id: periodVal,
          period: period.label.substring(0, 12),
          overall: stock.OverallRank * 100,
          cfirrExTfq: stock.CFIRREX_TTM_PctRank * 100,
          cfirrInTfq: stock.CFIRR_TTM_PctRank * 100,
          cfirrExChgTfqVsPq: stock.CFIRREXCHG_TTMvPQ_PctRank * 100,
          cfirrInChgTfqVsPq: stock.CFIRRCHG_TTMvPY_PctRank * 100,
          totInflAdj: stock.IAGFACHG_ANQvPY_PctRank * 100,
          currentRatio: stock.CURRENTRATIO_ANQ_PctRank * 100,
          revGrw: stock.REVENUECHG_TTMvPY_PctRank * 100,
          basicCFoverGrw: stock.BASICCFCHG_TTMvPY_PctRank * 100,
          evOverEbitda: stock.EVEBITDA_ANQ_PctRank * 100,
          pbook: stock.PBOOK_ANQ_PctRank * 100,
        };
      })
    );

    const results = await Promise.all(promises);

    // sort by id (period.value) after all requests finish
    const sortedData = results.filter(Boolean).sort((a, b) => a.id.localeCompare(b.id));

    setScorecardData(sortedData.reverse());
    setLoading(false);
  };

  fetchScorecardData();
}, [periodList, ticker]);


  //console.log((periodList));
  // console.log('scorecardData', scorecardData);

    return (
            <Box 
              sx={{
                mx: "0px",  
                width: "100%",
                minWidth: 530, 
                height: "100%",
                maxHeight: 600,         // ✔ fixed max height if you want it
                overflow: "hidden",       // scroll only when needed
                scrollbarWidth: "none",   // Firefox
                "&::-webkit-scrollbar": { display: "none" }, // Chrome/Safari
                borderRadius: 2,
              }}
            >
               <TableContainer 
                component={Paper} 
                sx={{ 
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "auto",
                  boxShadow: "none", 
                  pb: 1, 
                  pt: 0 }} >
                
                <Table stickyHeader size="small" sx={{
                    "& .MuiTableCell-root": {
                          padding: { xs: "0px 2px", sm: "0px 2px", lg: '0px, 0px' }, // tighter on mobile
                          fontSize: { xs: "0.55rem", sm: "0.55rem", lg: "0.55rem" },   // smaller font
                    }
                }}>
                  <TableHead>
                      {[{ label : "period", value : "index" }].map((period, id) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            responsiveStyle,
                            borderBottom: "1px solid #ffffffff",
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3, 
                            textTransform: "uppercase",
                          }}
                          key={id}
                        >
                          {ticker}
                        </TableCell>
                        {scorecardData.map((p) => (
                          <TableCell sx={{ 
                              responsiveStyle,
                              whiteSpace: "normal",
                              width: "auto",
                              maxWidth: '160px',
                              wordBreak:"break-word",
                              borderBottom: "1px solid #ffffffff",
                              fontWeight: "bold",
                              // whiteSpace: 'nowrap',
                              padding: 0
                            }} 
                          >
                            <div
                                style={{
                                    textAlign: "center",
                                    fontSize: "0.65rem",
                                    whiteSpace: "normal", 
                                    lineHeight: "1.1rem",
                                }}
                            >
                                {/* {formatDate(p[period.value])} */}
                            {(() => {
                                const formatted = formatDate(p[period.label]); 
                                const [monthDay, year] = formatted.split(", ");
                                return (
                                <>
                                    {monthDay}
                                    <br />
                                    {year}
                                </>
                                );
                            })()}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Overall" , field: "overall" },
                      { label: "", field: "" },
                      { label: "Return on Capital", field: "" },
                      { label: "CFIRR Ex % (TFQ)", field: "cfirrExTfq" },
                      { label: "CFIRR In % (TFQ)", field: "cfirrInTfq" },
                      { label: "CFIRR Ex Chg % (TFQ vs PQ)", field: "cfirrExChgTfqVsPq" },
                      { label: "CFIRR In Chg % (TFQ vs PQ)", field: "cfirrInChgTfqVsPq" },
                      { label: "", field: "" },
                      { label: "Balance Sheet", field: "" },
                      { label: "Tot Infl Adj Gross Cap Grw % (LFQ)", field: "totInflAdj" },
                      { label: "Current Ratio (LFQ)", field: "currentRatio" },
                      { label: "", field: "" },
                      { label: "Growth", field: "" },
                      { label: "Rev Grw % (TFQ vs SQPY)", field: "revGrw" },
                      { label: "Basic CF / Shr Grw % (TFQ vs SQPY)", field: "basicCFoverGrw" },
                      { label: "", field: "" },
                      { label: "Valuation", field: "" },
                      { label: "EV / EBITDA (TFQ)", field: "evOverEbitda" },
                      { label: "P / Book (LFQ)", field: "pbook" },       
                    ].map((rowItem, rowIdx) => (
                      <TableRow
                        key={rowIdx}
                        sx={{
                          "& td": { fontSize: "12px", borderBottom: "1px solid #ffffffff", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          responsiveStyle,
                          fontWeight: headers.includes(rowItem.label) ? 'bold' : 'normal',
                          position: "sticky",
                          backgroundColor: "white",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          }}
                        >
                          {(rowItem.label)}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {scorecardData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            responsiveStyle,
                            backgroundColor: backgroundColor(row[rowItem.field]),
                            color: "white",
                            textAlign: "right"
                          }}>
                            {formatValue(row[rowItem.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
    );    
}